import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import Decimal from 'decimal.js';
import { InvoiceProcessingActionTypeEnum } from 'anchor-ts-infra/src/dto/invoice/dto';
import { FeesByEnumDTO, PaymentMethodEnumDTO, PaymentProcessorTypeEnumDTO, RefundStatusTypeEnumDTO } from 'anchor-ts-infra/src/dto/payment/dto';
import { ApprovalMethodType, InvoiceApiStatusType } from 'anchor-scout-common/types';
import { ApprovalMethodType as ExternalApprovalMethodType, InvoiceStatusType as ExternalInvoiceStatusType, PaymentMethod as ExternalPaymentMethod, PaymentProcessor as ExternalPaymentProcessor, ProcessingActionType as ExternalProcessingActionType, RefundStatus as ExternalRefundStatus } from 'anchor-scout-common/types/external-api/invoice';
import { filterPaymentDetailsForClient, fromExternalDisplayStatus } from '../invoice-summary/translator';
import { InvoiceAccountingIntegration } from '../InvoiceAccountingIntegration';
import { InvoiceIntegrationModel, InvoiceIntegrationStatus } from '../InvoiceIntegrationModel';
import { MoneyModel } from '../MoneyModel';
import { PaymentModel } from '../PaymentModel';
import { formatDate } from '../time/selectors';
export { filterPaymentDetailsForClient };
function amountOr(amount, fallback) {
    return amount != null && amount !== '' ? new Decimal(amount) : fallback;
}
function centsToAmount(cents, fallbackCents) {
    return amountOr(cents, new Decimal(fallbackCents)).div(100).toDP(2);
}
function legacyTaxStatus(taxStatus) {
    if ((taxStatus === null || taxStatus === void 0 ? void 0 : taxStatus.type) !== 'appliedTax') return {
        type: 'none'
    };
    const { label, totalRate } = taxStatus.appliedTax;
    return {
        type: 'appliedTax',
        appliedTax: {
            label,
            totalRate
        }
    };
}
function isVoidedFromDisputeStatus(status) {
    return status.type === InvoiceApiStatusType.Voided && status.voided.previousStatusType === InvoiceApiStatusType.PaymentDisputed;
}
export function fromLegacyDTO(isVendor, dto) {
    var _dto_legalTermsDocument, _dto_showLineItemPeriod, _dto_coverCreditCardFees, _dto_referenceFields;
    // TODO: Meital - dto.customerId is unused; drop from InvoiceApi when EXTERNAL_INVOICES_ENDPOINT FF is fully rolled out.
    // TODO: Meital - dto.number (numeric) is unused; only formattedNumber is read. Drop from InvoiceApi when EXTERNAL_INVOICES_ENDPOINT FF is fully rolled out.
    return {
        id: dto.id,
        attachments: dto.attachments || [],
        netTerms: dto.netTerms,
        createdOn: dto.createdOn,
        issuedOn: dto.issuedOn,
        dueDate: dto.dueDate,
        isRetryable: dto.isRetryable,
        pdfExists: dto.pdfExists,
        totalAmount: dto.totalAmount,
        formattedNumber: dto.formattedNumber,
        // TODO: Meital - voided.userId and paidExternally.updatedBy ride through on this status pass-through but are never read; drop from InvoiceApiStatus when EXTERNAL_INVOICES_ENDPOINT FF is fully rolled out.
        status: isVendor ? dto.vendorStatus : dto.customerStatus,
        isVoidedFromDispute: isVoidedFromDisputeStatus(isVendor ? dto.vendorStatus : dto.customerStatus),
        isTotalAmountZero: dto.isTotalAmountZero,
        integration: InvoiceIntegrationModel.fromScoutDTO(dto.integration),
        items: dto.items,
        // TODO: Meital - relationship.title is unused; drop from InvoiceApi.relationship when EXTERNAL_INVOICES_ENDPOINT FF is fully rolled out.
        relationship: _object_spread_props(_object_spread({}, dto.relationship), {
            customer: {
                id: dto.customerId,
                name: dto.relationship.customer.name
            }
        }),
        rawTotalAmount: new Decimal(dto.rawTotalAmount).div(100).toDP(2),
        subtotal: centsToAmount(dto.subtotal, dto.rawTotalAmount),
        tax: centsToAmount(dto.tax, '0'),
        taxStatus: legacyTaxStatus(dto.taxStatus),
        rawDueDate: new Date(dto.rawDueDate),
        rawIssuedOn: new Date(dto.rawIssuedOn),
        rawCreatedOn: new Date(dto.createdOn),
        xeroIntegration: InvoiceAccountingIntegration.fromDTO(dto.xeroIntegration),
        // TODO: Meital - Refund.userId and Refund.refundFailed ride through on this payment pass-through but are never read; drop from Refund when EXTERNAL_INVOICES_ENDPOINT FF is fully rolled out.
        payment: dto.payment ? new PaymentModel(dto.payment) : null,
        displayStatusV2: dto.displayStatusV2,
        paymentDetails: isVendor ? dto.paymentDetails : filterPaymentDetailsForClient(dto.paymentDetails),
        isAdHoc: dto.isAdHoc,
        paymentMethodDisplay: dto.paymentMethodDisplay,
        legalTermsDocument: (_dto_legalTermsDocument = dto.legalTermsDocument) !== null && _dto_legalTermsDocument !== void 0 ? _dto_legalTermsDocument : null,
        showLineItemPeriod: (_dto_showLineItemPeriod = dto.showLineItemPeriod) !== null && _dto_showLineItemPeriod !== void 0 ? _dto_showLineItemPeriod : false,
        coverCreditCardFees: (_dto_coverCreditCardFees = dto.coverCreditCardFees) !== null && _dto_coverCreditCardFees !== void 0 ? _dto_coverCreditCardFees : null,
        originalPaymentTimestamp: dto.originalPaymentTimestamp,
        referenceFields: (_dto_referenceFields = dto.referenceFields) !== null && _dto_referenceFields !== void 0 ? _dto_referenceFields : []
    };
}
export function fromDTO(isVendor, dto) {
    var _dto_paymentMethodDisplay, _dto_legalTermsDocument, _dto_showLineItemPeriod, _dto_coverCreditCardFees, _dto_referenceFields;
    const totalAmountDecimal = new Decimal(dto.totalAmount);
    const taxDecimal = amountOr(dto.tax, new Decimal(0));
    const issueDate = new Date(dto.issueDate);
    const dueDate = new Date(dto.dueDate);
    const createdAt = new Date(dto.createdAt);
    const additionalInfo = dto.detailedStatus.additionalInfo;
    const paymentDetails = isVendor ? additionalInfo : filterPaymentDetailsForClient(additionalInfo);
    return {
        id: dto.id,
        attachments: dto.attachments.map((att)=>({
                id: att.id,
                displayName: att.name,
                fileSize: att.fileSize
            })),
        netTerms: dto.netTerms,
        createdOn: formatDate(dto.createdAt),
        issuedOn: formatDate(dto.issueDate),
        dueDate: formatDate(dto.dueDate),
        isRetryable: dto.canRetryPayment,
        pdfExists: dto.pdfAvailable,
        totalAmount: new MoneyModel(totalAmountDecimal).display(),
        formattedNumber: dto.invoiceNumber,
        status: externalDetailedStatusToApiStatus(dto.detailedStatus),
        isVoidedFromDispute: dto.isVoidedFromDispute,
        isTotalAmountZero: totalAmountDecimal.isZero(),
        integration: detailIntegrationToModel(dto.integrations.quickbooks),
        items: dto.lineItems.map(mapLineItem),
        relationship: {
            id: dto.agreement.id,
            title: dto.agreement.name,
            customer: {
                id: dto.client.business.id,
                name: dto.client.business.name
            },
            vendor: {
                name: dto.serviceProvider.business.name
            }
        },
        rawTotalAmount: totalAmountDecimal,
        subtotal: amountOr(dto.subtotal, totalAmountDecimal),
        tax: taxDecimal,
        taxStatus: dto.appliedTax != null ? {
            type: 'appliedTax',
            appliedTax: dto.appliedTax
        } : {
            type: 'none'
        },
        rawDueDate: dueDate,
        rawIssuedOn: issueDate,
        rawCreatedOn: createdAt,
        xeroIntegration: detailToInvoiceAccountingIntegration(dto.integrations.xero),
        payment: mapPayment(dto.payment),
        displayStatusV2: fromExternalDisplayStatus(dto.status),
        paymentDetails,
        isAdHoc: dto.isAdHoc,
        paymentMethodDisplay: (_dto_paymentMethodDisplay = dto.paymentMethodDisplay) !== null && _dto_paymentMethodDisplay !== void 0 ? _dto_paymentMethodDisplay : null,
        legalTermsDocument: (_dto_legalTermsDocument = dto.legalTermsDocument) !== null && _dto_legalTermsDocument !== void 0 ? _dto_legalTermsDocument : null,
        showLineItemPeriod: (_dto_showLineItemPeriod = dto.showLineItemPeriod) !== null && _dto_showLineItemPeriod !== void 0 ? _dto_showLineItemPeriod : false,
        coverCreditCardFees: (_dto_coverCreditCardFees = dto.coverCreditCardFees) !== null && _dto_coverCreditCardFees !== void 0 ? _dto_coverCreditCardFees : null,
        originalPaymentTimestamp: dto.originalPaymentTimestamp,
        referenceFields: (_dto_referenceFields = dto.referenceFields) !== null && _dto_referenceFields !== void 0 ? _dto_referenceFields : []
    };
}
function detailIntegrationToModel(status) {
    if (status == null) {
        return new InvoiceIntegrationModel({
            status: InvoiceIntegrationStatus.PendingSync
        });
    }
    return InvoiceIntegrationModel.fromExternalDTO(status);
}
function detailToInvoiceAccountingIntegration(status) {
    const integration = detailIntegrationToModel(status);
    return new InvoiceAccountingIntegration(integration, integration.isSynced());
}
function externalDetailedStatusToApiStatus(status) {
    switch(status.type){
        case ExternalInvoiceStatusType.Pending:
            return {
                type: InvoiceApiStatusType.Pending
            };
        case ExternalInvoiceStatusType.ProcessingCharge:
            return {
                type: InvoiceApiStatusType.ProcessingCharge
            };
        case ExternalInvoiceStatusType.PaymentConfirmed:
            return {
                type: InvoiceApiStatusType.PaymentConfirmed
            };
        case ExternalInvoiceStatusType.PaymentActionRequired:
            return {
                type: InvoiceApiStatusType.PaymentActionRequired,
                paymentActionRequired: {
                    processingAction: mapProcessingAction(status.paymentActionRequired.processingAction)
                }
            };
        case ExternalInvoiceStatusType.RequiresPaymentApproval:
            return {
                type: InvoiceApiStatusType.RequiresPaymentApproval,
                requiresPaymentApproval: {
                    timestamp: status.requiresPaymentApproval.timestamp != null ? new Date(status.requiresPaymentApproval.timestamp).getTime() : null,
                    approvalMethod: mapApprovalMethod(status.requiresPaymentApproval.approvalMethod)
                }
            };
        case ExternalInvoiceStatusType.PaymentFailed:
            return {
                type: InvoiceApiStatusType.PaymentFailed,
                paymentFailed: {
                    failureCode: status.paymentFailed.failureCode,
                    message: status.paymentFailed.message,
                    timestamp: new Date(status.paymentFailed.timestamp).getTime()
                }
            };
        case ExternalInvoiceStatusType.Paid:
            return {
                type: InvoiceApiStatusType.Paid,
                paid: {
                    paymentDate: new Date(status.paid.timestamp).getTime()
                }
            };
        case ExternalInvoiceStatusType.PaidExternally:
            return {
                type: InvoiceApiStatusType.PaidExternally,
                paidExternally: {
                    updatedBy: '',
                    timestamp: new Date(status.paidExternally.timestamp).getTime()
                }
            };
        case ExternalInvoiceStatusType.Voided:
            return {
                type: InvoiceApiStatusType.Voided,
                voided: {
                    userId: '',
                    timestamp: new Date(status.voided.timestamp).getTime(),
                    previousStatusType: undefined,
                    reason: undefined
                }
            };
        case ExternalInvoiceStatusType.Refunded:
            return {
                type: InvoiceApiStatusType.Refunded,
                refunded: {
                    timestamp: new Date(status.refunded.timestamp).getTime()
                }
            };
        case ExternalInvoiceStatusType.RefundFailed:
            return {
                type: InvoiceApiStatusType.RefundFailed,
                refundFailed: {
                    timestamp: new Date(status.refundFailed.timestamp).getTime(),
                    failureCode: status.refundFailed.failureCode
                }
            };
        case ExternalInvoiceStatusType.PaymentStopped:
            return {
                type: InvoiceApiStatusType.PaymentStopped,
                paymentStopped: {
                    timestamp: new Date(status.paymentStopped.timestamp).getTime(),
                    userId: status.paymentStopped.userId,
                    reason: status.paymentStopped.reason
                }
            };
        case ExternalInvoiceStatusType.PaymentDisputed:
            return {
                type: InvoiceApiStatusType.PaymentDisputed,
                paymentDisputed: {
                    timestamp: new Date(status.paymentDisputed.timestamp).getTime(),
                    failureCode: status.paymentDisputed.failureCode,
                    message: status.paymentDisputed.message
                }
            };
        case ExternalInvoiceStatusType.PaymentLateReturned:
            return {
                type: InvoiceApiStatusType.PaymentLateReturned,
                paymentLateReturned: {
                    timestamp: new Date(status.paymentLateReturned.timestamp).getTime(),
                    failureCode: status.paymentLateReturned.failureCode,
                    message: status.paymentLateReturned.message
                }
            };
        case ExternalInvoiceStatusType.PaymentDisputeWon:
            return {
                type: InvoiceApiStatusType.PaymentDisputeWon,
                paymentDisputeWon: {
                    timestamp: new Date(status.paymentDisputeWon.timestamp).getTime()
                }
            };
        case ExternalInvoiceStatusType.PartiallyRefunded:
            return {
                type: InvoiceApiStatusType.PartiallyRefunded,
                partiallyRefunded: {
                    timestamp: new Date(status.partiallyRefunded.timestamp).getTime()
                }
            };
    }
}
function mapApprovalMethod(method) {
    if (method == null) return null;
    switch(method.type){
        case ExternalApprovalMethodType.Manual:
            return {
                type: ApprovalMethodType.Manual
            };
        case ExternalApprovalMethodType.Automatic:
            return {
                type: ApprovalMethodType.Automatic,
                auto: method.auto
            };
    }
}
function mapProcessingAction(action) {
    var _action_url, _action_clientSecret;
    return {
        type: action.type === ExternalProcessingActionType.Sdk ? InvoiceProcessingActionTypeEnum.UseStripeSDK : InvoiceProcessingActionTypeEnum.RedirectToURL,
        url: (_action_url = action.url) !== null && _action_url !== void 0 ? _action_url : undefined,
        clientSecret: (_action_clientSecret = action.clientSecret) !== null && _action_clientSecret !== void 0 ? _action_clientSecret : undefined
    };
}
function mapLineItem(item) {
    return {
        id: item.id,
        name: item.name,
        description: item.description,
        quantity: item.quantity,
        rate: item.rate,
        totalAmount: item.amount,
        serviceId: item.serviceId,
        serviceTemplateId: item.serviceTemplateId,
        qbo: mapLineItemQbo(item.integrations),
        xero: mapLineItemXero(item.integrations),
        note: item.note,
        unitName: item.unitName,
        discount: item.discount,
        groupedLineItems: item.groupedLineItems.map(mapLineItem),
        subItems: item.subItems.map(mapSubItem),
        period: mapExternalPeriod(item.period),
        taxable: item.taxable,
        taxStatus: item.taxedAmount != null ? {
            type: 'taxedAmount',
            taxedAmount: item.taxedAmount
        } : {
            type: 'none'
        }
    };
}
function parseDatestampString(dateStr) {
    const [year, month, day] = dateStr.split('-').map(Number);
    return [
        year,
        month,
        day
    ];
}
function mapExternalPeriod(period) {
    if (period == null) return {
        type: 'delivery'
    };
    if (period.proRata == null) {
        return {
            type: 'fullTerm',
            from: parseDatestampString(period.from),
            to: parseDatestampString(period.to)
        };
    }
    return {
        type: 'partialTerm',
        from: parseDatestampString(period.from),
        to: parseDatestampString(period.to),
        proRata: period.proRata
    };
}
function mapLineItemQbo(integrations) {
    var _qbo_timeActivity_class;
    const qbo = integrations.quickbooks;
    if (qbo == null) return null;
    return {
        item: qbo.item,
        class: qbo.class,
        timeActivity: qbo.timeActivity ? {
            id: qbo.timeActivity.id,
            date: new Date(qbo.timeActivity.date).getTime(),
            note: qbo.timeActivity.note,
            hours: qbo.timeActivity.hours,
            minutes: qbo.timeActivity.minutes,
            item: qbo.timeActivity.item,
            customer: qbo.timeActivity.customer,
            class: (_qbo_timeActivity_class = qbo.timeActivity.class) !== null && _qbo_timeActivity_class !== void 0 ? _qbo_timeActivity_class : undefined
        } : null
    };
}
function mapLineItemXero(integrations) {
    const xero = integrations.xero;
    if (xero == null) return null;
    return {
        item: xero.item
    };
}
function mapSubItem(sub) {
    return {
        id: sub.id,
        name: sub.name,
        discount: sub.discount,
        totalAmount: sub.amount,
        rate: sub.rate,
        unitName: sub.unitName,
        origin: sub.origin,
        qbo: sub.integrations.quickbooks ? {
            item: sub.integrations.quickbooks.item,
            class: sub.integrations.quickbooks.class,
            timeActivity: sub.integrations.quickbooks.timeActivity
        } : null
    };
}
function mapPayment(payment) {
    if (payment == null) return null;
    return new PaymentModel({
        fees: mapPaymentFees(payment),
        paymentProcessorType: mapPaymentProcessor(payment.paymentProcessor),
        refunds: payment.refunds.map(mapRefund)
    });
}
function mapPaymentFees(payment) {
    switch(payment.method){
        case ExternalPaymentMethod.Ach:
            return {
                type: PaymentMethodEnumDTO.Ach
            };
        case ExternalPaymentMethod.CreditCard:
            return {
                type: PaymentMethodEnumDTO.CreditCard,
                creditCard: {
                    feesAmount: payment.creditCard.processingFee,
                    feesBy: payment.creditCard.feesPaidBy === 'vendor' ? FeesByEnumDTO.Vendor : FeesByEnumDTO.Customer
                }
            };
    }
}
function mapPaymentProcessor(processor) {
    return processor === ExternalPaymentProcessor.Stripe ? PaymentProcessorTypeEnumDTO.Stripe : PaymentProcessorTypeEnumDTO.Column;
}
function mapRefund(refund) {
    const amount = new Decimal(refund.amount);
    const refundFees = new Decimal(refund.refundFees);
    return {
        refundStatus: mapRefundStatus(refund.status),
        refundFailed: undefined,
        refundId: refund.id,
        timestamp: new Date(refund.timestamp).getTime(),
        reason: refund.reason,
        userId: undefined,
        amounts: {
            refundAmount: amount.mul(100).toNumber(),
            refundFees: refundFees.mul(100).toNumber(),
            refundNumber: refund.refundNumber,
            remainingAmount: new Decimal(refund.remainingAmount).mul(100).toNumber(),
            remainingFees: new Decimal(refund.remainingFees).mul(100).toNumber(),
            totalRefundAmount: amount.add(refundFees).mul(100).toNumber()
        }
    };
}
function mapRefundStatus(status) {
    switch(status){
        case ExternalRefundStatus.Succeeded:
            return RefundStatusTypeEnumDTO.Refunded;
        case ExternalRefundStatus.Pending:
            return RefundStatusTypeEnumDTO.Initiated;
        case ExternalRefundStatus.Canceled:
            return RefundStatusTypeEnumDTO.Cancelled;
        case ExternalRefundStatus.Failed:
        default:
            return RefundStatusTypeEnumDTO.Failed;
    }
}
